'use client'

/**
 * Read-only header chip for the per-user Country / Industry view, e.g.
 * "India · INR", "Europe · EUR", "Japan · JPY · Manufacturing". Hidden while the
 * view is All. It links to where the view is changed: Admin Settings for admin
 * and AP-Manager, the personal Settings page for everyone else.
 */

import Link from 'next/link'
import { useSession } from 'next-auth/react'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { useCountry } from '@/lib/country-context'
import { viewChipLabel, viewSettingsHref } from '@/lib/view-labels'

export function ViewChip({ className }: { className?: string }) {
  const { data: session } = useSession()
  const { view, displayCurrency, countryLabel, industryLabel } = useCountry()
  const label = viewChipLabel(view, {
    country: countryLabel(view.country),
    currency: displayCurrency,
    industry: industryLabel(view.industry),
  })
  if (!label) return null

  return (
    <Link
      href={viewSettingsHref(session?.roles)}
      aria-label={`Your view: ${label}. Change it in Settings.`}
      title='Change your view in Settings'
      // A 44px hit target (Rule 14) around a 32px pill.
      className={cn(
        'group inline-flex h-11 max-w-64 items-center rounded-full',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        className
      )}
    >
      <span
        className={cn(
          'inline-flex h-8 min-w-0 items-center gap-1.5 rounded-full border border-border/50 bg-card/60 px-3 text-xs',
          'font-medium text-foreground transition-colors group-hover:border-brand-cornflower/40 group-hover:bg-card'
        )}
      >
        <Icons.globe className='h-3.5 w-3.5 shrink-0 text-muted-foreground' aria-hidden='true' strokeWidth={1.5} />
        <span className='truncate'>{label}</span>
      </span>
    </Link>
  )
}
